import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { Bundle, FaresAvailable, Journey } from "@repo/ui/types/flights";

import { useAppSelector } from "../hooks";

export interface FlightsState {
  inboundJourneys: Journey[];
  outboundJourneys: Journey[];
  faresAvailable: FaresAvailable;
  bundlesByJourneyKey?: Record<string, Bundle[] | undefined>;
}

const initialState: FlightsState = {
  inboundJourneys: [] as Journey[],
  outboundJourneys: [] as Journey[],
  faresAvailable: {} as FaresAvailable,
  bundlesByJourneyKey: {},
};

const flightsAvailabilitySlice = createSlice({
  name: "flights",
  initialState,
  reducers: {
    setInboundJourneys: (state, action: PayloadAction<Journey[]>) => {
      state.inboundJourneys = action.payload;
      // set bundlesByJourneyKey for quick lookup
      action.payload.forEach((journey: Journey) => {
        if (journey.journeyKey && journey.bundleAvailability) {
          state.bundlesByJourneyKey![journey.journeyKey] = journey.bundleAvailability;
        }
      });
    },
    setOutboundJourneys: (state, action: PayloadAction<Journey[]>) => {
      state.outboundJourneys = action.payload;
      // set bundlesByJourneyKey for quick lookup
      action.payload.forEach((journey: Journey) => {
        if (journey.journeyKey && journey.bundleAvailability) {
          state.bundlesByJourneyKey![journey.journeyKey] = journey.bundleAvailability;
        }
      });
    },
    setFaresAvailable: (state, action: PayloadAction<FaresAvailable>) => {
      state.faresAvailable = action.payload;
    },
    resetFlights: () => initialState,
  },
});

export const { setInboundJourneys, setOutboundJourneys, setFaresAvailable, resetFlights } =
  flightsAvailabilitySlice.actions;

export default flightsAvailabilitySlice.reducer;

export const useInboundJourneys = () =>
  useAppSelector((state) => state.flightsAvailability.inboundJourneys);

export const useOutboundJourneys = () =>
  useAppSelector((state) => state.flightsAvailability.outboundJourneys);

export const useFaresAvailable = () =>
  useAppSelector((state) => state.flightsAvailability.faresAvailable);

// Helper to get bundles for a specific journey key
export const useBundlesForJourney = (journeyKey: string) =>
  useAppSelector((state) => state.flightsAvailability.bundlesByJourneyKey?.[journeyKey]);

// get the bundle details by journeyKey and bundleCode
export const useFlightBundleDetailByCode = (journeyKey: string, bundleCode: string) => {
  return useAppSelector((state) => {
    const bundles = state.flightsAvailability.bundlesByJourneyKey?.[journeyKey];
    return bundles?.find((bundle: Bundle) => bundle.bundleCode === bundleCode);
  });
};

// get inbound journey by journeyKey
export const useInboundJourneyByKey = (journeyKey: string) =>
  useAppSelector((state) =>
    state.flightsAvailability.inboundJourneys.find((journey) => journey.journeyKey === journeyKey),
  );

// get outbound journey by journeyKey
export const useOutboundJourneyByKey = (journeyKey: string) =>
  useAppSelector((state) =>
    state.flightsAvailability.outboundJourneys.find((journey) => journey.journeyKey === journeyKey),
  );
